import React from "react";
import styled from "@emotion/styled";
import { Toggle } from "@kompare/kompare-ui-library";
import { Divider, Flex, Grid, Tag, Text } from 'src/components';
import { colors } from "src/theme";
import { formatNumber, i18n } from "src/common";

const Logo = styled.img({
  maxHeight: '48px',
  minHeight: '26px',
  maxWidth: '142px',
  flexShrink: 0,
})

const TextAlignStyle = styled.div({
  textAlign: 'right'
})

/** e.g. "5% uštede" from API copy like "uštedi 5% popusta" */
export const parseSupplementarySavingsTag = (text: string | undefined | null): string | undefined => {
  if (!text) return undefined;
  const m = text.match(/(\d+)\s*%/);
  return m ? `${m[1]}% uštede` : undefined;
}

export interface OfferSupplementaryToggleRowProps {
  title: string;
  description?: string;
  checked: boolean;
  onToggleChange: (checked: boolean) => void;
}

/** Zeleni popust: naslov lijevo, zelena pill desno u redu; opis ispod (Figma). */
export const OfferSupplementaryToggleRow = ({ title, description, checked, onToggleChange }: OfferSupplementaryToggleRowProps) => {
  const savingsTag = parseSupplementarySavingsTag(description);
  return (
    <Grid columns="1fr auto" columnGap={8} align="center">
      <Flex vertical gap={4} style={{ minWidth: 0 }}>
        <Flex align="center" gap={8} wrap="wrap" style={{ width: '100%' }}>
          <Text type="base" bold color={colors.text1} style={{ flex: '1 1 auto', minWidth: '8rem' }}>{title}</Text>
          {savingsTag ? (
            <Flex align="center">
              <Tag
                value={savingsTag}
                background={colors.note3}
                textColor={colors.text3}
              />
            </Flex>
          ) : null}
        </Flex>
        {description ? (
          <Text type="tiny" color={colors.text3} style={{ minWidth: 0 }}>{description}</Text>
        ) : null}
      </Flex>
      <Toggle checked={checked} onChange={(c: any) => onToggleChange(c)} labelMarginBottom='0px' />
    </Grid>
  );
};

interface OfferHeaderCardProps {
  brandName?: string;
  offerLabel?: string;
  price?: number;
  priceType?: 'monthly' | 'yearly';
  discountPercentage?: number;
  logoUrl?: string;
  className?: string;
  handlePriceTypeChange?: () => void;
  infoOnly?: boolean;
  isComparisonFunnel?: boolean;
  diffPrice?: number;
}

export const OfferHeaderCard = ({ offerLabel, price, priceType, discountPercentage = 0, logoUrl, handlePriceTypeChange, brandName, infoOnly, isComparisonFunnel = false, diffPrice }: OfferHeaderCardProps) => {

  const renderComparisonPrice = () => {
    if (isComparisonFunnel && typeof diffPrice === 'number') {
      return diffPrice >= 0 ? (
        <Flex gap={4} justify='space-between' align='center' style={{background:'#ffc', borderRadius: '8px', padding: '4px 8px'}}>
          <Text type='small' color={colors.text2}>Ti štediš</Text>
          <Text type='base' color={colors.text2} bold={true}>+{formatNumber(diffPrice)}</Text>
        </Flex>
      ) : (
        <Flex gap={4} justify='space-between' align='center' style={{background:'#EBFBFF', borderRadius: '8px', padding: '4px 8px'}}>
          <Text type='small' color={colors.text2}>Ova polica skuplja je za</Text>
          <Text type='base' color={colors.text2} bold={true}>{formatNumber(Math.abs(diffPrice), '€')}</Text>
        </Flex>
      )
    }
    return null;
  }

  if (infoOnly) {
    return (
      <Grid columns='auto 1fr' columnGap={16} align='center'>
        <Logo src={logoUrl} alt="" />
        <Flex vertical gap={2} style={{ minWidth: 0 }}>
          {offerLabel && <Text type='base' bold>{offerLabel}</Text>}
          {brandName && <Text type='small' color={colors.text3}>{brandName}</Text>}
        </Flex>
      </Grid>
    );
  }
  return (
    <Flex vertical gap={12}>
      <Grid columns="1fr auto" columnGap={16} align="center">
        <Flex gap={12} align="center" style={{ minWidth: 0 }}>
          <Logo src={logoUrl} alt="logo" />
        </Flex>
        <TextAlignStyle>
          <Text type='xLarge' bold>{`${formatNumber(price)}€`}</Text>
          <Text type='tiny' color={colors.text3}>{i18n.t(`components.cards.offerHeaderCard.${priceType}`)}</Text>
        </TextAlignStyle>
      </Grid>
      {renderComparisonPrice()}
      <Divider dashed gap={0} color={colors.border5} />
      <Grid columns="1fr auto" columnGap={8} align="end">
        <Flex align="center" gap={8} wrap="wrap" style={{ minWidth: 0, width: '100%' }}>
          {offerLabel ? (
            <Text type="small" bold color={colors.text1} style={{ letterSpacing: '0.02em', flex: '0 1 auto' }}>{offerLabel}</Text>
          ) : null}
          <Flex
            gap={8}
            align="center"
            justify="flex-end"
            wrap="wrap"
            style={{
              marginLeft: 'auto',
              flexShrink: 0,
              textAlign: 'right',
            }}
          >
            <Text type="tiny" color={colors.text3}>
              {i18n.t(`components.cards.offerHeaderCard.${brandName?.toLowerCase() === 'groupama' ? 'totalYearlyGeneral' : 'totalYearly'}`)}
            </Text>
            {discountPercentage > 0 ? (
              <Tag
                value={`${discountPercentage}% uštede`}
                background={colors.offerSavingsPillBg}
                textColor={colors.offerSavingsPillText}
              />
            ) : null}
          </Flex>
        </Flex>
        <Toggle checked={priceType === 'yearly'} onChange={handlePriceTypeChange} disabled={isComparisonFunnel} labelMarginBottom='0px' />
      </Grid>
    </Flex>
  );
};
